import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  downloadExport,
  getStorageUsage,
  importAccount,
  ApiError,
  type ImportResult,
} from '../api';
import { ProgressBar } from './ProgressBar';

/**
 * Your data: how much room it takes, and how to take it with you.
 *
 * Two things that existed in the API and nowhere on screen. The quota — 5 GB per account, 25 MB
 * per file — was invisible until an upload was refused, which is the worst moment to learn a
 * limit exists. And there was no export at all: the only way to get a year of habit entries out
 * of a self-hosted application was `pg_dump` from a shell inside the container, which is a backup
 * story for whoever deployed it rather than whoever uses it.
 */

/** "1.4 GB" — bytes are for machines. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below ten, none above: "1.4 GB" and "512 MB" both read at a glance.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function YourData() {
  const queryClient = useQueryClient();
  const usage = useQuery({ queryKey: ['storage-usage'], queryFn: getStorageUsage });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  /**
   * Reading a file back in.
   *
   * Additive, and the wording says so before the button is pressed rather than after. Someone
   * reaching for an import is often mid-mishap, and "this adds, it does not replace" is the fact
   * that decides whether they should press it at all.
   */
  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const parsed: unknown = JSON.parse(await file.text());
      setResult(await importAccount(parsed));
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof SyntaxError
            ? 'That file is not readable JSON.'
            : 'Could not import that file.',
      );
    } finally {
      setBusy(false);
    }
  };

  const used = usage.data?.usedBytes ?? 0;
  const quota = usage.data?.quotaBytes ?? 0;
  const percent = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;

  return (
    <section className="card">
      <h3>Your data</h3>

      {usage.data && (
        <>
          <p className="muted">
            {formatBytes(used)} of {formatBytes(quota)} used by attachments — {percent}%. Single
            files are capped at {formatBytes(usage.data.maxFileBytes)}.
          </p>
          <ProgressBar
            percent={percent}
            testId="storage-bar"
            title={`${formatBytes(used)} of ${formatBytes(quota)}`}
          />
        </>
      )}

      <p className="muted">
        The export is every task, goal, habit, note, project and label in this account as JSON — raw
        rows, so the file stays readable by something other than this application. Labels come with
        their groups and their nesting, and a project comes with its members, so the filing and the
        sharing survive the round trip and not just the work. Somebody in your file who has no
        account here is dropped from the project and reported, rather than invented. Attachments are
        listed but their contents are not included. Passwords, sessions and tokens are deliberately
        left out.
      </p>

      <div className="detail-foot">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            downloadExport()
              .catch((e: unknown) =>
                setError(e instanceof ApiError ? e.message : 'Could not build the export.'),
              )
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Preparing…' : 'Download everything'}
        </button>
      </div>

      <h4 className="subtext">Bringing data in</h4>
      <p className="muted">
        Importing <strong>adds</strong> — it never replaces or merges. Everything in the file
        arrives as new rows alongside what is already here, so running the same file twice gives you
        two copies. That is deliberate: two copies are visible and fixable, where a silent overwrite
        of work done since the export would be neither.
      </p>

      <label className="subtext">
        Choose an export file
        <input
          type="file"
          accept="application/json,.json"
          aria-label="Import an export file"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Cleared so the same file can be chosen again after a failure.
            e.target.value = '';
            if (file) void onFile(file);
          }}
        />
      </label>

      {result && (
        <div className="banner ok-banner" data-testid="import-result">
          <p>
            Imported{' '}
            {Object.entries(result.imported)
              .map(([kind, n]) => `${n} ${kind}`)
              .join(', ') || 'nothing'}
            .
          </p>
          {/* One kind can carry several reasons (`task waits`, `taskLinks`); the pair is what is unique. */}
          {result.skipped.map((s) => (
            <p key={`${s.kind}\u0000${s.reason}`} className="subtext">
              Skipped {s.count} {s.kind} — {s.reason}
            </p>
          ))}
        </div>
      )}

      {error && <p className="error">{error}</p>}
    </section>
  );
}
